import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getEjercicios, getEmpresa, getPyg, getBalance, getProyeccionResultado } from "@/lib/api";
import { formatCurrency, cn } from "@/lib/utils";
import { TABLA_ANIOS, ColsAnios, COL_ANIO_EUR } from "@/components/ui/tabla-anios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Eye, EyeOff, Info } from "lucide-react";
import { codigoVisible, esCuentaDeAsiento, SimboloAsiento } from "@/lib/useModoAsiento";

// Clave de agrupación del detalle: por defecto los 3 primeros dígitos, PERO
// las cuentas de asiento manual (-AJ) conservan su código completo como clave
// para salir como fila propia — antes quedaban fundidas dentro del grupo de
// 3 dígitos y el ajuste "no se veía" (feedback Héctor sept-2026).
const grupoDetalle = (codigo) =>
  esCuentaDeAsiento(codigo) ? String(codigo) : String(codigo).substring(0, 3);

export default function ComparativaAniosPage() {
  const { empresaId } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [selectedAnios, setSelectedAnios] = useState([]);
  const [pygData, setPygData] = useState({});
  const [balanceData, setBalanceData] = useState({});
  const [loading, setLoading] = useState(true);
  // Histórico + proyectado en todas partes (regla 30-09): los años del
  // consolidado entran como columnas OPCIONALES marcadas "Proy." en amarillo corporativo.
  const [proy, setProy] = useState(null);
  const [selectedProyAnios, setSelectedProyAnios] = useState([]);
  // Balance histórico del AÑO BASE del modelo (ancla del reparto proyectado).
  const [balanceBase, setBalanceBase] = useState(null);
  // Base del año parcial, común a PyG y Balance (documento de cambios
  // visuales, oct-2026): acumulado real o previsión de cierre del modelo.
  const [base2026, setBase2026] = useState("acumulado");
  // Botón "ocultar filas a 0" (petición de Raúl 13-oct): por defecto las
  // filas a 0,00/sin dato en TODAS las columnas se ocultan; el usuario puede
  // enseñarlas. Preferencia recordada por navegador.
  const [ocultarCeros, setOcultarCeros] = useState(() => {
    try { return localStorage.getItem("finreport:comparativa-ocultar-ceros") !== "0"; }
    catch { return true; }
  });
  const toggleOcultarCeros = () => {
    setOcultarCeros((v) => {
      try { localStorage.setItem("finreport:comparativa-ocultar-ceros", v ? "0" : "1"); }
      catch { /* sin almacenamiento: solo esta sesión */ }
      return !v;
    });
  };

  useEffect(() => {
    loadData();
  }, [empresaId]);

  useEffect(() => {
    if (selectedAnios.length > 0 && ejercicios.length > 0) {
      loadFinancialData();
    }
  }, [selectedAnios, ejercicios]);

  const loadData = async () => {
    try {
      const [empresaRes, ejerciciosRes] = await Promise.all([
        getEmpresa(empresaId),
        getEjercicios(empresaId),
      ]);
      setEmpresa(empresaRes.data);
      // Solo ejercicios CON datos (misma regla que el motor): un ejercicio
      // creado sin importar "ganaba" como real a ceros y tapaba la columna
      // proyectada de ese año.
      const sortedEjercicios = ejerciciosRes.data
        .filter((e) => e.total_cuentas == null || e.total_cuentas > 0)
        .sort((a, b) => a.anio - b.anio);
      setEjercicios(sortedEjercicios);
      
      // Auto-select all years (we'll check if they have data when loading)
      const todosLosAnios = sortedEjercicios.map(e => e.anio);
      setSelectedAnios(todosLosAnios);
      // Proyección (tolerante: sin proyección, todo como siempre).
      try {
        const proyRes = await getProyeccionResultado(empresaId);
        setProy(proyRes.data);
        setSelectedProyAnios(proyRes.data?.anios_proyectados || []);
        // Balance HISTÓRICO del año base del modelo: hace falta SIEMPRE
        // (aunque ese año no esté seleccionado) para repartir la proyección
        // por la jerarquía A)/B)/C) con las reglas exactas del modelo.
        const base = [...(proyRes.data?.anios_historicos || [])].sort((x, y) => x - y).pop();
        const ejBase = sortedEjercicios.find((e) => e.anio === base);
        if (ejBase) {
          const bRes = await getBalance(ejBase.id);
          setBalanceBase({ anio: base, data: bRes.data });
        }
      } catch {
        setProy(null);
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialData = async () => {
    const newPygData = {};
    const newBalanceData = {};
    
    for (const anio of selectedAnios) {
      const ejercicio = ejercicios.find(e => e.anio === anio);
      if (ejercicio) {
        try {
          const [pygRes, balanceRes] = await Promise.all([
            getPyg(ejercicio.id),
            getBalance(ejercicio.id),
          ]);
          newPygData[anio] = pygRes.data;
          newBalanceData[anio] = balanceRes.data;
        } catch (error) {
          console.error(`Error loading data for ${anio}:`, error);
        }
      }
    }
    
    setPygData(newPygData);
    setBalanceData(newBalanceData);
  };

  const toggleAnio = (anio) => {
    setSelectedAnios(prev => {
      if (prev.includes(anio)) {
        return prev.filter(a => a !== anio);
      } else {
        return [...prev, anio].sort((a, b) => a - b);
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const sortedSelectedAnios = [...selectedAnios].sort((a, b) => a - b);

  // ── Años PROYECTADOS como entradas sintéticas con el MISMO shape ─────────
  // Fuente única: el consolidado. PyG: res.pyg (filas por partida PGC, todos
  // los años) + pyg_completo (agregados). Balance: solo las partidas que el
  // modelo calcula EXACTAMENTE (existencias, deudores, efectivo, acreedores,
  // deuda LP/CP, PN y los TOTALES); el resto muestra N/D — jamás un cero
  // inventado. El detalle por cuenta no existe en proyección ("—").
  const aniosProyTodos = proy?.anios_proyectados || [];
  const aniosProySel = aniosProyTodos.filter((a) => selectedProyAnios.includes(a)).sort((a, b) => a - b);
  const filaCompleta = (concepto, a) =>
    (proy?.pyg_completo || []).find((f) => f.concepto === concepto)?.por_anio?.[String(a)];
  const partidasBalProy = proy?.balance?.partidas || [];
  const balProy = (concepto, a) =>
    partidasBalProy.find((p) => p.concepto === concepto)?.por_anio?.[String(a)] ?? null;
  const sumaGrupoProy = (grupo, a) =>
    partidasBalProy
      .filter((p) => p.grupo === grupo && !p.sub)
      .reduce((acc, p) => acc + (p.por_anio?.[String(a)] ?? 0), 0);
  // ── Reparto del balance proyectado por la jerarquía histórica ────────────
  // Reglas EXACTAS del modelo (agrupar_balance_historico, tres_estados):
  //   Inmovilizado neto = partidas I+II+III del ANC (evoluciona agregado →
  //     sus sub-partidas no se desglosan a futuro: N/D).
  //   Existencias/Deudores/Efectivo/Acreedores ↔ conceptos del modelo 1:1.
  //   Deuda LP = "II. Deudas a l/p" + "III. Deudas con grupo l/p" (ídem c/p):
  //     solo se pinta en la sub-fila si la otra es 0 en el año base.
  //   "Otros activos/pasivos" = resto, CONSTANTES al año base → el resto de
  //     partidas no modeladas se arrastra plano desde el año base, que es
  //     exactamente lo que hace el modelo.
  //   PN exacto; sus sub-partidas se derivan de la mecánica declarada del
  //     modelo (resultado = neto publicado, reservas = retenidos, resto
  //     plano) con verjas — ver valoresSubPN. Si una verja falla: N/D.
  // Totales A)/B)/C) = suma de lo anterior, con CANDADO contra el total del
  // consolidado: si no cuadra al euro, los grupos vuelven a N/D (jamás se
  // enseña una cifra que no case con el modelo).
  const INMOV_PARTIDAS = [
    "I. Inmovilizado intangible",
    "II. Inmovilizado material",
    "III. Inversiones inmobiliarias",
  ];
  const EXACTAS_ACTIVO = {
    "I. Existencias": "Existencias",
    "II. Deudores comerciales y otras cuentas a cobrar": "Clientes",
    "VI. Efectivo y otros activos líquidos equivalentes": "Tesorería",
  };
  const L_DEUDA_LP = "II. Deudas a largo plazo";
  const L_GRUPO_LP = "III. Deudas con empresas del grupo y asociadas a l/p";
  const L_DEUDA_CP = "II. Deudas a corto plazo";
  const L_GRUPO_CP = "III. Deudas con empresas del grupo y asociadas a c/p";
  const L_ACREEDORES = "IV. Acreedores comerciales y otras cuentas a pagar";
  const L_ANC = "A) ACTIVO NO CORRIENTE";
  const L_AC = "B) ACTIVO CORRIENTE";
  const L_PN = "A) PATRIMONIO NETO";
  const L_PNC = "B) PASIVO NO CORRIENTE";
  const L_PC = "C) PASIVO CORRIENTE";

  const buscarEnArbol = (items, partida) => {
    for (const it of items || []) {
      if (it.partida === partida) return it;
      const f = buscarEnArbol(it.hijos, partida);
      if (f) return f;
    }
    return null;
  };
  const hBase = (seccion, partida) =>
    buscarEnArbol(balanceBase?.data?.[seccion]?.items, partida)?.saldo ?? 0;
  const anioBaseModelo = balanceBase?.anio;

  // E12 (oct-2026): eventos de "Otras hipótesis" por LÍNEA del balance
  // (ajuste acumulado publicado por el modelo). Cada movimiento aterriza en
  // SU fila del reparto (bug Edser: "Otros activos bajaba el total sin mover
  // ninguna línea").
  const evLinea = (partida, a) =>
    proy?.eventos_por_linea?.[partida]?.por_anio?.[String(a)] ?? 0;
  const evSerie = (serieKey, a) =>
    Object.values(proy?.eventos_por_linea || {}).reduce(
      (s, d) => s + (d?.serie === serieKey ? (d?.por_anio?.[String(a)] ?? 0) : 0), 0);

  // "Otros activos/pasivos" del modelo planos entre el año base y el año a,
  // DESCONTANDO los eventos declarados por línea (E12): un evento conocido ya
  // no rompe el reparto; solo un movimiento inexplicado manda los grupos a N/D.
  const otrosPlano = (concepto, a) => {
    const va = balProy(concepto, a);
    const vb = balProy(concepto, anioBaseModelo);
    const serieKey = concepto === "Otros activos" ? "otros_activos" : "otros_pasivos";
    return va != null && vb != null && Math.abs(va - evSerie(serieKey, a) - vb) <= 1;
  };

  // Valor proyectado de una partida del árbol histórico ("auto"), o heredado:
  // "plano" = arrastra el año base (no modelada), "nulo" = el modelo no la
  // desglosa a futuro.
  const valorPartidaProy = (seccion, partida, a, modo) => {
    if (modo === "nulo") return null;
    // E12: una línea no modelada = plana al base + SUS eventos declarados.
    if (modo === "plano") return hBase(seccion, partida) + evLinea(partida, a);
    if (seccion === "activo") {
      if (EXACTAS_ACTIVO[partida]) return balProy(EXACTAS_ACTIVO[partida], a);
      if (INMOV_PARTIDAS.includes(partida)) {
        // Héctor 6-oct ("siguen apareciendo N/D"): el modelo SÍ desglosa el
        // inmovilizado por tipo (evolucion_inmovilizado.por_tipo, W20.12):
        // I. = intangible; las inversiones inmobiliarias (III) van ENGLOBADAS
        // en el tipo material y el modelo no tiene operación que las mueva →
        // III plano al año base y II = material − III base (barrido 13-oct,
        // caso TAULERS SOTOCA: III != 0 ya no manda todo a N/D). Candados:
        // Σ tipos == Inmovilizado neto al euro (en años con tope de
        // amortización no cuadra → N/D) y II derivado no negativo.
        const bt = proy?.evolucion_inmovilizado?.por_tipo?.bloques;
        if (!bt) return null;
        // P13 (Pau oct-2026): se usa la serie SIN TOPE del backend — si la
        // amortización proyectada supera el valor pendiente, el neto sale
        // NEGATIVO y se PINTA (con aviso en Salud), en vez de esconder el
        // error del driver tras un N/D. Con tope, Σ tipos < total y las tres
        // líneas caían a N/D (caso Edser 2031).
        const bi = bt.intangible?.[String(a)];
        const bm = bt.material?.[String(a)];
        const i = bi?.final_sin_tope ?? bi?.final;
        const m = bm?.final_sin_tope ?? bm?.final;
        const total = balProy("Inmovilizado neto", a);
        if (i == null || m == null || total == null) return null;
        // E12 (corregido, revisión externa oct-2026): el backend IMPUTA los
        // eventos de las líneas de inmovilizado al por_tipo (todos a
        // "material"), así que i + m ya es el total — sumar aquí los eventos
        // otra vez mandaba las tres líneas a N/D con cualquier hipótesis.
        if (Math.abs(i + m - total) > 1) return null;
        // Para pintar cada evento en SU línea: se saca el acumulado de
        // eventos de "material" (donde el backend los mete todos) y se
        // recoloca por línea declarada.
        const evInmov = evSerie("inmovilizado", a);
        const mSinEventos = m - evInmov;
        const iiiBase = hBase("activo", "III. Inversiones inmobiliarias");
        if (partida === "I. Inmovilizado intangible") return i + evLinea(partida, a);
        if (partida === "II. Inmovilizado material") return mSinEventos - iiiBase + evLinea(partida, a);
        return iiiBase + evLinea(partida, a); // III: plana al base + sus eventos
      }
      return hBase(seccion, partida) + evLinea(partida, a);
    }
    if (partida === L_ACREEDORES) return balProy("Proveedores", a);
    // Deuda bancaria (II) y con grupo (III): el modelo lleva UNA serie
    // conjunta, pero NINGUNA operación suya toca la deuda con grupo →
    // III plana al año base y II = serie del modelo − III base (barrido
    // 13-oct, casos GARCIA FAURA y PAUFOR: ambas != 0 ya no manda las dos
    // filas a N/D). Verja: si la serie cae por debajo del tramo grupo, el
    // reparto no cierra → ambas a N/D.
    if (partida === L_DEUDA_LP || partida === L_GRUPO_LP) {
      // E12: un evento sobre la deuda con grupo aterriza en su línea III.
      const grupo = hBase(seccion, L_GRUPO_LP) + evLinea(L_GRUPO_LP, a);
      const v = balProy("Deuda financiera LP", a);
      if (v == null || v - grupo < -0.005) return null;
      return partida === L_GRUPO_LP ? grupo : v - grupo;
    }
    if (partida === L_DEUDA_CP || partida === L_GRUPO_CP) {
      const grupo = hBase(seccion, L_GRUPO_CP) + evLinea(L_GRUPO_CP, a);
      const v = balProy("Deuda financiera CP", a);
      if (v == null || v - grupo < -0.005) return null;
      return partida === L_GRUPO_CP ? grupo : v - grupo;
    }
    return hBase(seccion, partida) + evLinea(partida, a);
  };

  // E12: eventos de las líneas NO modeladas de cada grupo (las series
  // modeladas — existencias/deudores/acreedores/deuda/inmovilizado — ya
  // llevan los suyos dentro).
  const EV_LINEAS_RESTO = {
    [L_ANC]: ["IV. Inversiones en empresas del grupo y asociadas a l/p",
              "V. Inversiones financieras a largo plazo",
              "VI. Activos por impuesto diferido"],
    [L_AC]: ["III. Inversiones en empresas del grupo y asociadas a c/p",
             "IV. Inversiones financieras a corto plazo",
             "V. Periodificaciones a corto plazo (Activo)"],
    [L_PNC]: ["I. Provisiones a largo plazo",
              "IV. Pasivos por impuesto diferido",
              "V. Periodificaciones a largo plazo"],
    [L_PC]: ["I. Provisiones a corto plazo",
             "V. Periodificaciones a corto plazo (Pasivo)"],
  };
  const evResto = (grupo, a) =>
    (EV_LINEAS_RESTO[grupo] || []).reduce((s, p) => s + evLinea(p, a), 0);

  // Totales de grupo derivados (lo modelado a su valor + el resto plano + los
  // eventos declarados de sus líneas).
  const totalGrupoProy = (grupo, a) => {
    if (grupo === L_ANC) {
      if (!otrosPlano("Otros activos", a)) return null;
      const inmovBase = INMOV_PARTIDAS.reduce((s, p) => s + hBase("activo", p), 0);
      return (balProy("Inmovilizado neto", a) ?? 0) + hBase("activo", L_ANC) - inmovBase
        + evResto(L_ANC, a);
    }
    if (grupo === L_AC) {
      if (!otrosPlano("Otros activos", a)) return null;
      const modelados = ["Existencias", "Clientes", "Tesorería"]
        .reduce((s, c) => s + (balProy(c, a) ?? 0), 0);
      const modeladosBase = Object.keys(EXACTAS_ACTIVO)
        .reduce((s, p) => s + hBase("activo", p), 0);
      return modelados + hBase("activo", L_AC) - modeladosBase + evResto(L_AC, a);
    }
    const sec = "patrimonio_neto_pasivo";
    if (grupo === L_PNC) {
      if (!otrosPlano("Otros pasivos", a)) return null;
      return (balProy("Deuda financiera LP", a) ?? 0) + hBase(sec, L_PNC)
        - hBase(sec, L_DEUDA_LP) - hBase(sec, L_GRUPO_LP) + evResto(L_PNC, a);
    }
    if (grupo === L_PC) {
      if (!otrosPlano("Otros pasivos", a)) return null;
      return (balProy("Deuda financiera CP", a) ?? 0)
        + (balProy("Proveedores", a) ?? 0) + hBase(sec, L_PC)
        - hBase(sec, L_DEUDA_CP) - hBase(sec, L_GRUPO_CP) - hBase(sec, L_ACREEDORES)
        + evResto(L_PC, a);
    }
    return null;
  };

  // ── Sub-partidas del PN derivadas (13-oct: "N/D solo si de verdad no se
  // puede saber"). El modelo DECLARA que el PN solo se mueve por resultados
  // retenidos (PN = PN base + acumulado publicado) y publica el Resultado
  // del ejercicio proyectado. Con esa mecánica el desglose histórico se
  // proyecta sin inventar nada: Resultado del ejercicio = neto del año,
  // Reservas absorben lo retenido (resultados anteriores − dividendos, el
  // resto como plug) y las demás filas quedan planas al base — el mismo
  // patrón "modelado 1:1 + resto plano + candado" de los grupos. VERJAS:
  // hace falta exactamente UNA fila de Reservas y UNA de Resultado del
  // ejercicio en el árbol, el PN tiene que haberse movido SOLO por el
  // acumulado publicado (un evento de balance que toque el PN por otra vía
  // lo rompe → N/D) y la suma debe cuadrar al euro con el PN del modelo.
  const valoresSubPN = (a) => {
    const nodoPN = buscarEnArbol(balanceBase?.data?.patrimonio_neto_pasivo?.items, L_PN);
    if (!nodoPN?.hijos?.length) return null;
    let resNode = null, rdoNode = null, ambiguo = false;
    const scan = (its) => {
      for (const it of its || []) {
        if (/Resultado del ejercicio/i.test(it.partida)) {
          if (rdoNode) ambiguo = true; rdoNode = it;
        } else if (/\bReservas\b/i.test(it.partida)) {
          if (resNode) ambiguo = true; resNode = it;
        }
        scan(it.hijos);
      }
    };
    scan(nodoPN.hijos);
    if (!resNode || !rdoNode || ambiguo) return null;
    const pnA = balProy("Patrimonio neto", a);
    const pnB = balProy("Patrimonio neto", anioBaseModelo);
    const acum = partidasBalProy
      .find((p) => p.concepto === "Resultados acumulados no distribuidos (proyección)")
      ?.por_anio?.[String(a)];
    const netoA = filaCompleta("Resultado neto", a);
    if (pnA == null || pnB == null || acum == null || netoA == null) return null;
    // E12: el PN puede moverse por resultados retenidos Y por eventos
    // declarados (ampliación de capital, aportaciones…); solo un movimiento
    // INEXPLICADO manda el sub-PN a N/D.
    const evPNTotal = evSerie("patrimonio_neto", a);
    if (Math.abs(pnA - pnB - acum - evPNTotal) > 1) return null;
    const rdoBase = rdoNode.saldo ?? 0;
    const plugReservas = (resNode.saldo ?? 0) + evLinea(resNode.partida, a)
      + (pnA - pnB) - (netoA - rdoBase) - evPNTotal;
    const map = new Map();
    const anular = (its) => { for (const it of its || []) { map.set(it.partida, null); anular(it.hijos); } };
    const compute = (it) => {
      let v;
      if (it === rdoNode) { v = netoA; anular(it.hijos); }
      else if (it === resNode) { v = plugReservas; anular(it.hijos); } // su detalle interno no es derivable
      else if (it.hijos?.length) v = it.hijos.reduce((s, h) => s + compute(h), 0);
      else v = (it.saldo ?? 0) + evLinea(it.partida, a); // E12: su evento, si lo hay
      map.set(it.partida, v);
      return v;
    };
    const total = nodoPN.hijos.reduce((s, h) => s + compute(h), 0);
    if (Math.abs(total - pnA) > 1) return null; // candado al euro
    return map;
  };

  // Lista PLANA de partidas proyectadas de una sección (grupos + sub-filas),
  // recorriendo el árbol del año base para cubrir TODA la jerarquía visible.
  const construirItemsProy = (seccion, a) => {
    const grupos = seccion === "activo" ? [L_ANC, L_AC] : [L_PN, L_PNC, L_PC];
    const out = grupos.map((g) => ({
      partida: g,
      saldo: (g === L_PN ? balProy("Patrimonio neto", a) : totalGrupoProy(g, a)) ?? null,
      hijos: [],
      cuentas: [],
    }));
    // Sub-PN: derivado del propio modelo si las verjas lo permiten; si no,
    // al menos el Resultado del ejercicio (publicado EXACTO) se pinta.
    const subPN = seccion === "activo" ? null : valoresSubPN(a);
    const walk = (items, modo) => {
      for (const it of items || []) {
        if (grupos.includes(it.partida)) {
          walk(it.hijos, it.partida === L_PN ? "pn" : "auto");
          continue;
        }
        if (modo === "pn") {
          const saldoPN = subPN
            ? subPN.get(it.partida) ?? null
            : /Resultado del ejercicio/i.test(it.partida)
              ? filaCompleta("Resultado neto", a) ?? null
              : null;
          out.push({ partida: it.partida, saldo: saldoPN, hijos: [], cuentas: [] });
          walk(it.hijos, "pn");
          continue;
        }
        const saldo = valorPartidaProy(seccion, it.partida, a, modo);
        out.push({ partida: it.partida, saldo: saldo ?? null, hijos: [], cuentas: [] });
        const esModelada = EXACTAS_ACTIVO[it.partida] || it.partida === L_ACREEDORES ||
          [L_DEUDA_LP, L_GRUPO_LP, L_DEUDA_CP, L_GRUPO_CP].includes(it.partida) ||
          INMOV_PARTIDAS.includes(it.partida);
        walk(it.hijos, modo !== "auto" ? modo : esModelada ? "nulo" : "plano");
      }
    };
    walk(balanceBase?.data?.[seccion]?.items, "auto");
    // CANDADO: los grupos derivados suman el total del consolidado al euro;
    // si no, vuelven a N/D (las sub-filas exactas del modelo se mantienen).
    const filasGrupo = out.filter((o) => grupos.includes(o.partida));
    const total = sumaGrupoProy(seccion === "activo" ? "activo" : "pasivo_pn", a);
    if (filasGrupo.some((o) => o.saldo == null) ||
        Math.abs(filasGrupo.reduce((s, o) => s + o.saldo, 0) - total) > 1) {
      for (const o of filasGrupo) {
        if (o.partida !== L_PN) o.saldo = null; // el PN es exacto: se conserva
      }
    }
    return out;
  };
  // Un año puede ser a la vez ejercicio REAL y año PROYECTADO (empresa con
  // 2026 importado pero año base 2025: el modelo lo re-proyecta). GANA EL
  // EJERCICIO REAL: son datos importados, completos — la síntesis proyectada
  // tiene N/D en todo lo no derivable y NUNCA debe tapar datos reales
  // (regresión del 13-oct: la primera versión del dedupe hacía ganar a la
  // proyectada y Héctor vio N/D donde antes había cifras). Para ver la
  // proyección de ese año basta desmarcarlo en el grupo "Histórico:".
  // Año PARCIAL (p. ej. 2026 a 30/09): con base "Previsión de cierre" su
  // columna muestra el año proyectado en lugar del acumulado real.
  const ejParcial = ejercicios.find(
    (e) => e.fecha_inicio && e.fecha_fin &&
      !(String(e.fecha_inicio).slice(5, 10) === "01-01" && String(e.fecha_fin).slice(5, 10) === "12-31")
  );
  const anioParcial = ejParcial?.anio ?? null;
  const fechaCorte = ejParcial?.fecha_fin
    ? String(ejParcial.fecha_fin).slice(0, 10).split("-").reverse().join("/")
    : null;
  const previsionParcial = base2026 === "prevision" && anioParcial != null &&
    sortedSelectedAnios.includes(anioParcial) && aniosProySel.includes(anioParcial);
  const aniosHistCols = sortedSelectedAnios.filter((a) => !(previsionParcial && a === anioParcial));
  const aniosProyCols = aniosProySel.filter((a) => !aniosHistCols.includes(a));
  const pygDataFull = { ...pygData };
  const balanceDataFull = { ...balanceData };
  aniosProyCols.forEach((a) => {
    const k = String(a);
    pygDataFull[a] = {
      proyectado: true,
      // Las partidas que el modelo NO proyecta (2, 3, 9-12 y 15-17) van a 0
      // POR DISEÑO DECLARADO en los supuestos del consolidado — ese 0 no es
      // inventado: es el supuesto del modelo, igual que en A.1/A.2. (Héctor
      // 13-oct: salían N/D y parecía un fallo.)
      partidas: (proy?.pyg || [])
        .filter((f) => /^\d/.test(String(f.partida)))
        .map((f) => ({ partida: f.partida, saldo: f.por_anio?.[k] ?? 0, cuentas: [] })),
      resultado_explotacion: filaCompleta("EBIT", a),
      resultado_financiero: filaCompleta("Resultado financiero", a),
      resultado_antes_impuestos: filaCompleta("BAI", a),
      resultado_ejercicio: filaCompleta("Resultado neto", a),
      ebitda: proy?.kpis?.ebitda?.por_anio?.[k] ?? null,
    };
    balanceDataFull[a] = {
      proyectado: true,
      activo: {
        total: sumaGrupoProy("activo", a),
        items: construirItemsProy("activo", a),
      },
      patrimonio_neto_pasivo: {
        total: sumaGrupoProy("pasivo_pn", a),
        items: construirItemsProy("patrimonio_neto_pasivo", a),
      },
    };
  });
  const aniosCols = [...aniosHistCols, ...aniosProyCols];
  const acumuladoVisible = anioParcial != null && aniosHistCols.includes(anioParcial);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comparativa-anios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Comparativa Años
          </h1>
          <p className="text-muted-foreground mt-1">
            {empresa?.nombre} - PyG y Balance por ejercicio
          </p>
        </div>
      </div>

      {/* Selector: todos los controles en una línea */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <span className="text-[13px] font-semibold">Seleccionar ejercicios</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Histórico:</span>
              {ejercicios.map((ej) => (
                <label key={ej.anio} className="flex cursor-pointer items-center gap-1.5 text-[13px]">
                  <Checkbox
                    id={`anio-${ej.anio}`}
                    checked={selectedAnios.includes(ej.anio)}
                    onCheckedChange={() => toggleAnio(ej.anio)}
                  />
                  {ej.anio}
                </label>
              ))}
            </div>
            {aniosProyTodos.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-l border-border pl-5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-proy">Proyección:</span>
                {aniosProyTodos.map((a) => (
                  <label key={a} className="flex cursor-pointer items-center gap-1.5 text-[13px] text-proy">
                    <Checkbox
                      id={`anio-proy-${a}`}
                      checked={selectedProyAnios.includes(a)}
                      onCheckedChange={() =>
                        setSelectedProyAnios((prev) =>
                          prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a].sort()
                        )
                      }
                      data-testid={`select-anio-proy-${a}`}
                    />
                    {a}{a === anioParcial ? " Proy." : ""}
                  </label>
                ))}
              </div>
            )}
            <div className="pill-group">
              {aniosProyTodos.length > 0 && (
                <>
                  <button className="pill"
                    onClick={() => { setSelectedAnios(ejercicios.map((e) => e.anio)); setSelectedProyAnios([...aniosProyTodos]); }}>
                    Todo
                  </button>
                  <button className="pill"
                    onClick={() => { setSelectedAnios(ejercicios.map((e) => e.anio)); setSelectedProyAnios([]); }}>
                    Solo histórico
                  </button>
                  <button className="pill"
                    onClick={() => { setSelectedAnios([]); setSelectedProyAnios([...aniosProyTodos]); }}>
                    Solo proyección
                  </button>
                </>
              )}
              <button className={cn("pill", !ocultarCeros && "pill-active")} onClick={toggleOcultarCeros}
                data-testid="toggle-filas-cero"
                title="Las filas a 0,00 € o sin dato en todas las columnas no aportan nada; puedes mostrarlas u ocultarlas">
                {ocultarCeros
                  ? (<><Eye className="h-3.5 w-3.5" />Mostrar filas a 0</>)
                  : (<><EyeOff className="h-3.5 w-3.5" />Ocultar filas a 0</>)}
              </button>
            </div>
            {anioParcial != null && (
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-medium text-muted-foreground">Base {anioParcial}:</span>
                <div className="pill-group" data-testid="base-parcial">
                  <button className={cn("pill", base2026 === "acumulado" && "pill-active")} onClick={() => setBase2026("acumulado")}>
                    Histórico acumulado
                  </button>
                  <button className={cn("pill", base2026 === "prevision" && "pill-active")} onClick={() => setBase2026("prevision")}>
                    Previsión de cierre
                  </button>
                </div>
              </div>
            )}
          </div>
          {anioParcial != null && (
            <p className="text-[11px] text-muted-foreground">Base {anioParcial} común para PyG y Balance.</p>
          )}
        </CardContent>
      </Card>

      {aniosCols.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Selecciona al menos un ejercicio para ver la comparativa
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Cuenta de Pérdidas y Ganancias */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Cuenta de Pérdidas y Ganancias</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {acumuladoVisible && (
                <div className="flex w-fit items-center gap-2 rounded-xl border border-[hsl(var(--proy)/0.35)] bg-proy px-3 py-1.5 text-[12px]">
                  <Info className="h-3.5 w-3.5 text-proy" />
                  {anioParcial} acumulado{fechaCorte ? ` · fecha de corte ${fechaCorte}` : ""} · periodo parcial
                </div>
              )}
              <div className="overflow-x-auto">
                <table className={cn(TABLA_ANIOS, "text-sm")}>
                  <ColsAnios n={aniosCols.length} anio={COL_ANIO_EUR} />
                  <CabeceraAnios aniosHist={aniosHistCols} aniosProy={aniosProyCols} anioParcial={anioParcial} fechaCorte={fechaCorte} />
                  <tbody>
                    <PygComparativaRows
                      anios={aniosCols}
                      pygData={pygDataFull}
                      ocultarCeros={ocultarCeros}
                    />
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Balance de Situación */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Balance de Situación</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className={cn(TABLA_ANIOS, "text-sm")}>
                  <ColsAnios n={aniosCols.length} anio={COL_ANIO_EUR} />
                  <CabeceraAnios aniosHist={aniosHistCols} aniosProy={aniosProyCols} anioParcial={anioParcial} fechaCorte={fechaCorte} />
                  <tbody>
                    <BalanceComparativaRows
                      anios={aniosCols}
                      balanceData={balanceDataFull}
                      ocultarCeros={ocultarCeros}
                    />
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// Cabecera agrupada HISTÓRICO / PROYECCIÓN (misma línea visual que la
// Comparativa Ratios); el año parcial se rotula "Acumulado" con su fecha de corte.
function CabeceraAnios({ aniosHist, aniosProy, anioParcial, fechaCorte }) {
  return (
    <thead>
      <tr className="text-[11px] uppercase tracking-wider">
        <th rowSpan={2} className="border-b px-3 py-2 text-left font-semibold">Partida</th>
        {aniosHist.length > 0 && (
          <th colSpan={aniosHist.length} className="border-b bg-muted/60 px-3 py-1.5 text-center font-semibold text-muted-foreground">Histórico</th>
        )}
        {aniosProy.length > 0 && (
          <th colSpan={aniosProy.length} className="border-b bg-proy-strong px-3 py-1.5 text-center font-semibold text-proy">Proyección</th>
        )}
      </tr>
      <tr className="border-b">
        {aniosHist.map((anio) => (
          <th key={anio} className="px-3 py-2 text-right font-semibold">
            {anio === anioParcial ? `${anio} · Acumulado` : anio}
            {anio === anioParcial && fechaCorte && (
              <div className="text-[10px] font-normal text-muted-foreground">Corte {fechaCorte}</div>
            )}
          </th>
        ))}
        {aniosProy.map((anio) => (
          <th key={anio} className="bg-proy px-3 py-2 text-right font-semibold text-proy">
            {anio === anioParcial ? `${anio} · Previsión cierre` : anio}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function PygComparativaRows({ anios, pygData, ocultarCeros = true }) {
  const primerAnio = anios[0];
  // Filas-ruido fuera (13-oct, pantallazo de Raúl en BICA SELFSTORAGE): una
  // partida a 0,00 € o sin dato en TODAS las columnas no cuenta nada — ni en
  // histórico ni en proyección, donde su 0 es el supuesto declarado del
  // modelo. Se oculta entera (desactivable con el botón "Mostrar filas a
  // 0"); en cuanto algún año trae importe, la fila vuelve sola. Los totales
  // A.x y el EBITDA se muestran siempre.
  const conDatos = (partida) =>
    !ocultarCeros ||
    anios.some((anio) => {
      const p = pygData[anio]?.partidas?.find((x) => x.partida === partida.partida);
      return p?.saldo != null && Math.abs(p.saldo) >= 0.005;
    });
  const partidas = (pygData[primerAnio]?.partidas || []).filter(conDatos);

  // Filtrar partidas por sección
  const partidasExplotacion = partidas.filter(p => {
    const num = parseInt(p.partida);
    return num >= 1 && num <= 12;
  });
  
  const partidasFinancieras = partidas.filter(p => {
    const num = parseInt(p.partida);
    return num >= 13 && num <= 17;
  });
  
  const partidaImpuestos = partidas.filter(p => {
    const num = parseInt(p.partida);
    return num === 18 || num === 19;
  });

  return (
    <>
      {/* Partidas 1-12: Explotación */}
      {partidasExplotacion.map((partida, idx) => (
        <PygPartidaRow 
          key={idx} 
          partida={partida} 
          anios={anios} 
          pygData={pygData}
        />
      ))}
      
      {/* A.1) RESULTADO DE EXPLOTACIÓN */}
      <tr className="bg-muted/50 font-semibold">
        <td className="py-2 px-3">A.1) RESULTADO DE EXPLOTACIÓN</td>
        {anios.map(anio => {
          const valor = pygData[anio]?.resultado_explotacion ?? (pygData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono ${pygData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {valor === null ? '—' : formatCurrency(valor)}
            </td>
          );
        })}
      </tr>
      
      {/* Partidas 13-17: Financieras */}
      {partidasFinancieras.map((partida, idx) => (
        <PygPartidaRow 
          key={idx} 
          partida={partida} 
          anios={anios} 
          pygData={pygData}
        />
      ))}
      
      {/* A.2) RESULTADO FINANCIERO */}
      <tr className="bg-muted/50 font-semibold">
        <td className="py-2 px-3">A.2) RESULTADO FINANCIERO</td>
        {anios.map(anio => {
          const valor = pygData[anio]?.resultado_financiero ?? (pygData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono ${pygData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {valor === null ? '—' : formatCurrency(valor)}
            </td>
          );
        })}
      </tr>
      
      {/* A.3) RESULTADO ANTES DE IMPUESTOS */}
      <tr className="bg-muted/50 font-semibold">
        <td className="py-2 px-3">A.3) RESULTADO ANTES DE IMPUESTOS</td>
        {anios.map(anio => {
          const valor = pygData[anio]?.resultado_antes_impuestos ?? (pygData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono ${pygData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {valor === null ? '—' : formatCurrency(valor)}
            </td>
          );
        })}
      </tr>
      
      {/* Partida 18/19: Impuestos */}
      {partidaImpuestos.map((partida, idx) => (
        <PygPartidaRow 
          key={idx} 
          partida={partida} 
          anios={anios} 
          pygData={pygData}
        />
      ))}
      
      {/* A.4) RESULTADO DEL EJERCICIO */}
      <tr className="bg-primary/10 font-bold">
        <td className="py-2 px-3">A.4) RESULTADO DEL EJERCICIO</td>
        {anios.map(anio => {
          const valor = pygData[anio]?.resultado_ejercicio ?? (pygData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono ${pygData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {valor === null ? '—' : formatCurrency(valor)}
            </td>
          );
        })}
      </tr>
      
      {/* Separador */}
      <tr><td colSpan={anios.length + 1} className="py-2"></td></tr>
      
      {/* EBITDA */}
      <tr className="bg-blue-100 dark:bg-blue-950">
        <td className="py-2 px-3 font-semibold">EBITDA</td>
        {anios.map(anio => {
          const valor = pygData[anio]?.ebitda ?? (pygData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono font-semibold ${pygData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
              {valor === null ? '—' : formatCurrency(valor)}
            </td>
          );
        })}
      </tr>
    </>
  );
}

function PygPartidaRow({ partida, anios, pygData }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasCuentas = partida.cuentas && partida.cuentas.length > 0;

  // Función para agrupar cuentas por los primeros 3 dígitos
  const getCuentasAgrupadas = (cuentas) => {
    if (!cuentas) return [];
    const grupos = {};
    for (const cuenta of cuentas) {
      const codigo3 = cuenta.codigo.substring(0, 3);
      if (!grupos[codigo3]) {
        grupos[codigo3] = { codigo: codigo3, nombre: cuenta.nombre, saldo: 0 };
      }
      grupos[codigo3].saldo += cuenta.saldo || 0;
    }
    return Object.values(grupos).sort((a, b) => a.codigo.localeCompare(b.codigo));
  };

  // Obtener la lista de códigos únicos (3 dígitos) de todos los años
  const getAllCodigos3 = () => {
    const codigos = new Set();
    for (const anio of anios) {
      const partidaAnio = pygData[anio]?.partidas?.find(p => p.partida === partida.partida);
      if (partidaAnio?.cuentas) {
        for (const cuenta of partidaAnio.cuentas) {
          codigos.add(grupoDetalle(cuenta.codigo));
        }
      }
    }
    return Array.from(codigos).sort();
  };

  const codigosUnicos = getAllCodigos3();

  // Obtener el nombre de una cuenta para un código de 3 dígitos
  const getNombreCuenta = (codigo3) => {
    for (const anio of anios) {
      const partidaAnio = pygData[anio]?.partidas?.find(p => p.partida === partida.partida);
      if (partidaAnio?.cuentas) {
        const cuenta = partidaAnio.cuentas.find(c => grupoDetalle(c.codigo) === codigo3);
        if (cuenta) return cuenta.nombre;
      }
    }
    return "";
  };

  return (
    <>
      <tr 
        className={`border-b hover:bg-muted/30 cursor-pointer ${hasCuentas ? '' : ''}`}
        onClick={() => hasCuentas && setIsOpen(!isOpen)}
      >
        <td className="py-2 px-3 flex items-center gap-2">
          {hasCuentas && (
            <span className="text-muted-foreground">
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
          )}
          {!hasCuentas && <span className="w-4" />}
          <span className="font-medium">{partida.partida}</span>
        </td>
        {anios.map(anio => {
          const partidaAnio = pygData[anio]?.partidas?.find(p => p.partida === partida.partida);
          // En años proyectados un null es "el modelo no proyecta esta
          // partida" → N/D, jamás un cero inventado.
          const valor = partidaAnio?.saldo ?? (pygData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono ${pygData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) < 0 ? 'text-rose-600' : (valor ?? 0) > 0 ? 'text-emerald-600' : ''}`}>
              {formatCurrency(valor)}
            </td>
          );
        })}
      </tr>

      {/* Detalle cuentas - agrupadas por 3 dígitos */}
      {isOpen && codigosUnicos.map((codigo3, idx) => (
        <tr key={idx} className="bg-muted/20 text-xs">
          <td className="py-1 px-3 pl-10 text-muted-foreground">
            {codigoVisible(codigo3)}
            {esCuentaDeAsiento(codigo3) && <SimboloAsiento className="ml-1" />}
            {" - "}{getNombreCuenta(codigo3)}
          </td>
          {anios.map(anio => {
            // El detalle por cuenta no existe en los años proyectados.
            if (pygData[anio]?.proyectado) {
              return (
                <td key={anio} className="py-1 px-3 text-right font-mono text-muted-foreground bg-proy">—</td>
              );
            }
            const partidaAnio = pygData[anio]?.partidas?.find(p => p.partida === partida.partida);
            // Sumar todas las cuentas que empiecen con este código de 3 dígitos
            let valor = 0;
            if (partidaAnio?.cuentas) {
              for (const cuenta of partidaAnio.cuentas) {
                if (grupoDetalle(cuenta.codigo) === codigo3) {
                  valor += cuenta.saldo || 0;
                }
              }
            }
            return (
              <td key={anio} className={`py-1 px-3 text-right font-mono ${valor < 0 ? 'text-rose-600' : ''}`}>
                {formatCurrency(valor)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function BalanceComparativaRows({ anios, balanceData, ocultarCeros = true }) {
  const primerAnio = anios[0];
  const activo = balanceData[primerAnio]?.activo || { items: [], total: 0 };
  const pnp = balanceData[primerAnio]?.patrimonio_neto_pasivo || { items: [], total: 0 };

  // Misma regla anti-ruido que el PyG (13-oct): una partida a 0/sin dato en
  // TODAS las columnas (y sin ningún descendiente con datos) no se pinta —
  // un N/D de proyección no "salva" la fila si el histórico tampoco trae
  // nada. Las cabeceras ACTIVO / PN Y PASIVO se muestran siempre.
  const buscarValor = (items, partida) => {
    for (const it of items || []) {
      if (it.partida === partida) return it.saldo;
      const f = buscarValor(it.hijos, partida);
      if (f != null) return f;
    }
    return null;
  };
  const podar = (section) => {
    const vivo = (item) =>
      anios.some((anio) => {
        const v = buscarValor(balanceData[anio]?.[section]?.items, item.partida);
        return v != null && Math.abs(v) >= 0.005;
      }) || (item.hijos || []).some(vivo);
    const rec = (items) => (items || []).filter(vivo).map((it) => ({ ...it, hijos: rec(it.hijos) }));
    return ocultarCeros ? rec : (items) => items || [];
  };
  const activoItems = podar("activo")(activo.items);
  const pnpItems = podar("patrimonio_neto_pasivo")(pnp.items);

  return (
    <>
      {/* ACTIVO Header */}
      <tr className="bg-blue-100 dark:bg-blue-950 font-bold">
        <td className="py-2 px-3">ACTIVO</td>
        {anios.map(anio => (
          <td key={anio} className="py-2 px-3 text-right font-mono">
            {formatCurrency(balanceData[anio]?.activo?.total || 0)}
          </td>
        ))}
      </tr>
      
      {/* Activo items */}
      {activoItems.map((item, idx) => (
        <BalanceItemRow 
          key={idx} 
          item={item} 
          anios={anios} 
          balanceData={balanceData}
          section="activo"
          level={0}
        />
      ))}
      
      {/* Espacio */}
      <tr><td colSpan={anios.length + 1} className="py-2"></td></tr>
      
      {/* PATRIMONIO NETO Y PASIVO Header */}
      <tr className="bg-green-100 dark:bg-green-950 font-bold">
        <td className="py-2 px-3">PATRIMONIO NETO Y PASIVO</td>
        {anios.map(anio => (
          <td key={anio} className="py-2 px-3 text-right font-mono">
            {formatCurrency(balanceData[anio]?.patrimonio_neto_pasivo?.total || 0)}
          </td>
        ))}
      </tr>
      
      {/* PNP items */}
      {pnpItems.map((item, idx) => (
        <BalanceItemRow 
          key={idx} 
          item={item} 
          anios={anios} 
          balanceData={balanceData}
          section="patrimonio_neto_pasivo"
          level={0}
        />
      ))}
    </>
  );
}

function BalanceItemRow({ item, anios, balanceData, section, level }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.hijos && item.hijos.length > 0;
  const hasCuentas = item.cuentas && item.cuentas.length > 0;
  const isExpandable = hasChildren || hasCuentas;
  
  const paddingLeft = 12 + level * 16;

  // Función para buscar el valor de una partida en un año específico
  const findItemValue = (anio, targetPartida, items) => {
    for (const it of items) {
      if (it.partida === targetPartida) {
        return it.saldo;
      }
      if (it.hijos) {
        const found = findItemValue(anio, targetPartida, it.hijos);
        if (found !== null) return found;
      }
    }
    return null;
  };

  return (
    <>
      <tr 
        className={`border-b hover:bg-muted/30 ${isExpandable ? 'cursor-pointer' : ''} ${level === 0 ? 'font-semibold' : ''}`}
        onClick={() => isExpandable && setIsOpen(!isOpen)}
      >
        <td className="py-2 px-3" style={{ paddingLeft: `${paddingLeft}px` }}>
          <div className="flex items-center gap-2">
            {isExpandable && (
              <span className="text-muted-foreground">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </span>
            )}
            {!isExpandable && <span className="w-4" />}
            <span>{item.partida}</span>
          </div>
        </td>
        {anios.map(anio => {
          const sectionData = balanceData[anio]?.[section]?.items || [];
          const encontrado = findItemValue(anio, item.partida, sectionData);
          // Proyección: solo las partidas que el modelo calcula exactamente;
          // el resto N/D (nunca un cero inventado).
          const valor = encontrado ?? (balanceData[anio]?.proyectado ? null : 0);
          return (
            <td key={anio} className={`py-2 px-3 text-right font-mono ${balanceData[anio]?.proyectado ? "bg-proy " : ""}${(valor ?? 0) < 0 ? 'text-rose-600' : ''}`}>
              {formatCurrency(valor)}
            </td>
          );
        })}
      </tr>
      
      {/* Hijos */}
      {isOpen && hasChildren && item.hijos.map((hijo, idx) => (
        <BalanceItemRow 
          key={idx}
          item={hijo}
          anios={anios}
          balanceData={balanceData}
          section={section}
          level={level + 1}
        />
      ))}
      
      {/* Cuentas detalle */}
      {isOpen && hasCuentas && item.cuentas.map((cuenta, idx) => (
        <tr key={idx} className="bg-muted/20 text-xs">
          <td className="py-1 px-3 text-muted-foreground" style={{ paddingLeft: `${paddingLeft + 24}px` }}>
            {codigoVisible(cuenta.codigo)}
            {esCuentaDeAsiento(cuenta.codigo) && <SimboloAsiento className="ml-1" />}
            {" - "}{cuenta.nombre}
          </td>
          {anios.map(anio => {
            // El detalle por cuenta no existe en los años proyectados.
            if (balanceData[anio]?.proyectado) {
              return (
                <td key={anio} className="py-1 px-3 text-right font-mono text-muted-foreground bg-proy">—</td>
              );
            }
            // Buscar la cuenta en el año correspondiente
            const sectionData = balanceData[anio]?.[section]?.items || [];
            const findCuentaValue = (items, targetPartida, targetCodigo) => {
              for (const it of items) {
                if (it.partida === targetPartida && it.cuentas) {
                  const c = it.cuentas.find(c => c.codigo === targetCodigo);
                  if (c) return c.saldo;
                }
                if (it.hijos) {
                  const found = findCuentaValue(it.hijos, targetPartida, targetCodigo);
                  if (found !== null) return found;
                }
              }
              return null;
            };
            const valor = findCuentaValue(sectionData, item.partida, cuenta.codigo) || 0;
            return (
              <td key={anio} className={`py-1 px-3 text-right font-mono ${valor < 0 ? 'text-rose-600' : ''}`}>
                {formatCurrency(valor)}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
